import { motion } from 'framer-motion';
import { TechStack } from '../components/ui/TechStack';
import { BentoGrid, BentoGridItem } from '../components/ui/BentoGrid';
import { Timeline } from '../components/sections/Timeline';
import { useRef } from 'react';
import { GlassCard } from '../components/ui/GlassCard';
import { Code2, Brain, Rocket, Shield, Database, Cpu, Activity } from 'lucide-react';

const bentoItems = [
    {
        title: "Autonomous Intelligence",
        description: "Designing self-correcting agentic systems that bridge the gap between intent and execution.",
        icon: <Brain className="w-8 h-8 text-emerald-400" />,
        className: "md:col-span-2 md:row-span-2 bg-emerald-500/5",
    },
    {
        title: "Cloud Infrastructure",
        description: "Architecting scalable distributed systems with Docker and Kubernetes.",
        icon: <Shield className="w-6 h-6 text-sky-400" />,
        className: "md:col-span-1 md:row-span-1 bg-sky-500/5",
    },
    {
        title: "Agentic Workflows",
        description: "Pioneering the future of automation through multi-agent collaboration.",
        icon: <Rocket className="w-6 h-6 text-purple-400" />,
        className: "md:col-span-1 md:row-span-1 bg-purple-500/5",
    },
    {
        title: "Full-Stack Development",
        description: "Mastering the entire stack from low-level systems to high-performance UIs.",
        icon: <Code2 className="w-6 h-6 text-orange-400" />,
        className: "md:col-span-1 md:row-span-2 bg-orange-500/5",
    },
    {
        title: "Data Pipelines",
        description: "Building resilient ETL pipelines processing millions of events in real-time.",
        icon: <Database className="w-6 h-6 text-blue-400" />,
        className: "md:col-span-1 md:row-span-1 bg-blue-500/5",
    },
    {
        title: "Embedded Systems",
        description: "Exploring the intersection of hardware and software with IoT and real-time kernels.",
        icon: <Cpu className="w-6 h-6 text-red-400" />,
        className: "md:col-span-1 md:row-span-1 bg-red-500/5",
    }
];

export function AboutMe() {
    const containerRef = useRef<HTMLDivElement>(null);

    return (
        <div ref={containerRef} className="bg-tech-bg min-h-screen">
            {/* Hero Section of About Me */}
            <section className="relative h-[80vh] flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/10 via-transparent to-tech-bg" />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-emerald-500/5 rounded-full blur-[120px]" />
                </div>

                <div className="relative z-10 text-center px-4 max-w-4xl">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1.2, ease: "easeOut" }}
                    >
                        <h2 className="text-[10vw] md:text-[6vw] font-black text-white leading-none tracking-tighter mb-8">
                            AUTONOMOUS <br />
                            <span className="text-emerald-500">ARCHITECT.</span>
                        </h2>
                        <p className="text-xl md:text-2xl text-gray-400 font-light tracking-wide max-w-2xl mx-auto">
                            Deconstructing complexity into elegant, high-performance systems that live at the edge of possibility.
                        </p>
                    </motion.div>
                </div>

                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-px h-24 bg-gradient-to-b from-emerald-500 to-transparent" />
            </section>

            {/* Biography Section */}
            <section className="py-32 px-6 md:px-12 max-w-7xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-20 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="relative"
                    >
                        <div className="absolute -inset-4 bg-emerald-500/10 blur-2xl rounded-2xl" />
                        <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-emerald-500/20">
                            <img
                                src="/paras-lehana-portrait-visionary-architect-neural-neon.png"
                                alt="Paras Lehana - Visionary Architect"
                                className="w-full h-full object-cover grayscale brightness-75 hover:grayscale-0 hover:brightness-100 transition-all duration-1000"
                            />
                            <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-tech-bg to-transparent" />
                        </div>
                    </motion.div>

                    <div className="space-y-12">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                        >
                            <h3 className="text-3xl font-bold text-white mb-6 uppercase tracking-wider">The Vision</h3>
                            <div className="space-y-6 text-gray-400 text-lg leading-relaxed font-light">
                                <p>
                                    I am <span className="text-emerald-400 font-semibold">Paras Lehana</span>, a Full-Stack Engineer and System Architect focused on building the infrastructure of the autonomous future.
                                </p>
                                <p>
                                    With over 6 years of experience across the entire technical spectrum, I specialize in <span className="bg-emerald-500/10 text-emerald-400 px-2 rounded">Agentic AI</span>, distributed systems, and cinematic user interfaces.
                                </p>
                                <p>
                                    My philosophy is simple: <span className="italic text-white">Code is poetry, and architecture is the canvas.</span> Every line I write is optimized for performance, scalability, and aesthetic impact.
                                </p>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Expertise Bento Grid */}
            <section className="py-24 max-w-7xl mx-auto px-6">
                <div className="mb-16 text-center">
                    <h2 className="text-4xl md:text-5xl font-black text-white mb-4 tracking-tighter uppercase">
                        VERSATILE <span className="text-emerald-500">EXPERTISE</span>
                    </h2>
                </div>
                <BentoGrid>
                    {bentoItems.map((item, i) => (
                        <BentoGridItem
                            key={i}
                            title={item.title}
                            description={item.description}
                            icon={item.icon}
                            className={item.className}
                        />
                    ))}
                </BentoGrid>
            </section>

            {/* Journey Timeline */}
            <div className="bg-black/20">
                <Timeline />
            </div>

            {/* Operational Stats Section */}
            <section className="py-24 border-y border-white/5 relative bg-black/40">
                <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-12 text-center">
                    {[
                        { label: "LINES_OF_CODE", value: "250K+", icon: <Code2 className="w-4 h-4" /> },
                        { label: "COMMITS_SYNCED", value: "1.2K", icon: <Database className="w-4 h-4" /> },
                        { label: "NODES_DEPLOYED", value: "48", icon: <Cpu className="w-4 h-4" /> },
                        { label: "UPTIME_RECORD", value: "99.9%", icon: <Activity className="w-4 h-4" /> }
                    ].map((stat, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.1 }}
                            className="space-y-2"
                        >
                            <div className="flex justify-center text-emerald-500 mb-2 opacity-30">{stat.icon}</div>
                            <div className="text-3xl md:text-5xl font-black text-white italic">{stat.value}</div>
                            <div className="text-[10px] font-mono text-gray-500 tracking-[0.3em]">{stat.label}</div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Life Protocols (Hobbies) */}
            <section className="py-32 max-w-7xl mx-auto px-6">
                <div className="mb-20">
                    <h2 className="text-4xl md:text-6xl font-black text-white tracking-tighter">
                        LIFE <span className="text-emerald-500">PROTOCOLS</span>
                    </h2>
                    <p className="font-mono text-gray-500 text-xs tracking-widest mt-4">Offline execution routines.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {[
                        { title: "DIGITAL_ART", img: "/visionary-landscape-digital-cityscape-future-architect.png", desc: "Exploring aesthetics in virtual spaces." },
                        { title: "NEURAL_READING", img: "/paras-lehana-lifestyle-architect-coding-at-dawn-cinematic.png", desc: "Consuming technical theory & system patterns." },
                        { title: "WORLD_SYNC", img: "/paras-lehana-portrait-visionary-architect-neural-neon.png", desc: "Strategic travel & cultural observation." }
                    ].map((hobby, i) => (
                        <GlassCard key={i} className="group overflow-hidden border-white/5 bg-black/40">
                            <div className="aspect-video relative overflow-hidden">
                                <img src={hobby.img} alt={hobby.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                                <div className="absolute inset-0 bg-emerald-500/20 mix-blend-overlay opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                            <div className="p-6">
                                <span className="text-[9px] font-mono text-emerald-500/50 block mb-2 tracking-[0.3em]">{hobby.title}</span>
                                <p className="text-sm text-gray-400 font-mono tracking-tight">{hobby.desc}</p>
                            </div>
                        </GlassCard>
                    ))}
                </div>
            </section>

            {/* Technical Arsenal (TechStack) */}
            <section className="py-24">
                <TechStack />
            </section>

            {/* Call to Action */}
            <section className="py-32 text-center relative overflow-hidden">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-emerald-500/10 rounded-full blur-[120px] pointer-events-none" />

                <h2 className="text-4xl md:text-5xl font-black text-white mb-12 tracking-tighter relative z-10 transition-transform">
                    READY TO BUILD THE <br />
                    <span className="text-emerald-500 uppercase">Next Generation?</span>
                </h2>

                <div className="relative z-10">
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="px-12 py-5 bg-emerald-500 text-black font-black uppercase tracking-widest rounded-full shadow-[0_0_40px_rgba(16,185,129,0.4)] hover:shadow-[0_0_60px_rgba(16,185,129,0.6)] transition-all duration-300"
                    >
                        Initialize Contact →
                    </motion.button>
                </div>
            </section>
        </div>
    );
}
